// ===== Document Field Extraction Types =====

export const DOCUMENT_TYPES = ['purchase_order', 'receipt', 'invoice'] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export interface DocumentFieldOption {
  key: string;
  label: string;
  description?: string;
  isDefault: boolean;
}

// Header fields: POModel (po_number, vendor_name_extracted, subtotal, total, tax_total, currency)
// Line fields:   POLineModel (sku, description, quantity_ordered, unit_price, total, uom)
export const PURCHASE_ORDER_FIELDS: DocumentFieldOption[] = [
  { key: 'po_number', label: 'PO Number', description: 'Purchase order identifier', isDefault: true },
  { key: 'vendor_name', label: 'Vendor Name', description: 'Name of the vendor', isDefault: true },
  { key: 'subtotal', label: 'Subtotal', description: 'Subtotal before tax', isDefault: true },
  { key: 'total', label: 'Total', description: 'Total PO amount', isDefault: true },
  { key: 'tax_total', label: 'Tax Total', description: 'Total tax amount', isDefault: true },
  { key: 'currency', label: 'Currency', description: 'Currency code', isDefault: true },
  { key: 'line_items.sku', label: 'Line Item SKU', description: 'Stock keeping unit', isDefault: true },
  { key: 'line_items.description', label: 'Line Item Description', description: 'Description of line item', isDefault: true },
  { key: 'line_items.quantity', label: 'Line Item Quantity', description: 'Quantity ordered', isDefault: true },
  { key: 'line_items.unit_price', label: 'Line Item Unit Price', description: 'Price per unit', isDefault: true },
  { key: 'line_items.total', label: 'Line Item Total', description: 'Total for line item', isDefault: true },
  { key: 'line_items.uom', label: 'Line Item UOM', description: 'Unit of measure', isDefault: true },
];

// Header fields: ReceiptDocumentModel (external_receipt_number, vendor_name_extracted, document_date, po_numbers, subtotal)
// Line fields:   ReceiptDocumentLineModel (description, sku, qty, uom, unit_price, total)
export const RECEIPT_FIELDS: DocumentFieldOption[] = [
  { key: 'receipt_number', label: 'Receipt Number', description: 'Receipt identifier', isDefault: true },
  { key: 'vendor_name', label: 'Vendor Name', description: 'Name of the vendor', isDefault: true },
  { key: 'receipt_date', label: 'Receipt Date', description: 'Date of the receipt document', isDefault: true },
  { key: 'po_number', label: 'PO Number', description: 'Referenced purchase order number', isDefault: true },
  { key: 'subtotal', label: 'Subtotal', description: 'Subtotal before tax', isDefault: true },
  { key: 'line_items.description', label: 'Line Item Description', description: 'Description of line item', isDefault: true },
  { key: 'line_items.sku', label: 'Line Item SKU', description: 'Stock keeping unit', isDefault: true },
  { key: 'line_items.quantity', label: 'Line Item Quantity', description: 'Quantity received', isDefault: true },
  { key: 'line_items.unit_price', label: 'Line Item Unit Price', description: 'Price per unit', isDefault: true },
  { key: 'line_items.total', label: 'Line Item Total', description: 'Total for line item', isDefault: true },
  { key: 'line_items.uom', label: 'Line Item UOM', description: 'Unit of measure', isDefault: true },
];

// Header fields: InvoiceModel (external_invoice_number, vendor_name_extracted, invoice_date, due_date, payment_terms, currency, subtotal, total, tax_total)
// Line fields:   InvoiceLineModel (description, quantity, uom, unit_price, total, po_number)
export const INVOICE_FIELDS: DocumentFieldOption[] = [
  { key: 'invoice_number', label: 'Invoice Number', description: 'Invoice identifier', isDefault: true },
  { key: 'vendor_name', label: 'Vendor Name', description: 'Name of the vendor', isDefault: true },
  { key: 'vendor_gst_number', label: 'GST Number', description: 'Document-level GST/HST/TPS registration number', isDefault: true },
  { key: 'vendor_qst_number', label: 'QST Number', description: 'Document-level QST/TVQ registration number', isDefault: false },
  { key: 'po_number', label: 'PO Number', description: 'Purchase order or project reference number', isDefault: true },
  { key: 'subtotal', label: 'Subtotal', description: 'Subtotal before tax', isDefault: true },
  { key: 'total', label: 'Total', description: 'Total amount due', isDefault: true },
  { key: 'discount', label: 'Discount', description: 'Total discount amount', isDefault: true },
  { key: 'additional_charges', label: 'Additional Charges', description: 'Shipping or other extra charges', isDefault: true },
  { key: 'tax_total', label: 'Tax Total', description: 'Total tax amount', isDefault: true },
  { key: 'tax_items', label: 'Tax Lines', description: 'Individual tax line breakdowns', isDefault: false },
  { key: 'currency', label: 'Currency', description: 'Currency code', isDefault: true },
  { key: 'invoice_date', label: 'Invoice Date', description: 'Date of the invoice', isDefault: true },
  { key: 'due_date', label: 'Due Date', description: 'Payment due date', isDefault: true },
  { key: 'payment_terms', label: 'Payment Terms', description: 'Terms printed on the invoice', isDefault: false },
  { key: 'line_items.description', label: 'Line Item Description', description: 'Description of line item', isDefault: true },
  { key: 'line_items.quantity', label: 'Line Item Quantity', description: 'Quantity invoiced', isDefault: true },
  { key: 'line_items.unit_price', label: 'Line Item Unit Price', description: 'Price per unit', isDefault: true },
  { key: 'line_items.total', label: 'Line Item Total', description: 'Total for line item', isDefault: true },
  { key: 'line_items.uom', label: 'Line Item UOM', description: 'Unit of measure', isDefault: true },
  { key: 'line_items.po_number', label: 'Line Item PO Number', description: 'PO number as printed on invoice line', isDefault: true },
];

export const FIELDS_BY_DOC_TYPE: Record<DocumentType, DocumentFieldOption[]> = {
  purchase_order: PURCHASE_ORDER_FIELDS,
  receipt: RECEIPT_FIELDS,
  invoice: INVOICE_FIELDS,
};

// ===== Vendor Fields =====

export interface VendorFieldOption {
  key: string;
  label: string;
  description?: string;
  isDefault: boolean;
  alwaysOn?: boolean;
}

export const VENDOR_FIELDS: VendorFieldOption[] = [
  { key: 'legal_name', label: 'Legal Name', description: 'Official legal name of the vendor', isDefault: true, alwaysOn: true },
  { key: 'company_name', label: 'Company Name', description: 'Trade or company name', isDefault: false },
  { key: 'print_on_check_name', label: 'Print On Check Name', description: 'Name shown on printed checks', isDefault: false },
  { key: 'tax_ids', label: 'Tax ID', description: 'Tax identification number', isDefault: true },
  { key: 'default_payment_terms', label: 'Default Payment Terms', description: 'Standard payment terms for the vendor', isDefault: true },
  { key: 'address', label: 'Address', description: 'Vendor mailing address', isDefault: false },
  { key: 'bank_account', label: 'Bank Account', description: 'Bank account reference used for payments', isDefault: false },
  { key: 'phone', label: 'Phone', description: 'Vendor phone number', isDefault: false },
  { key: 'mobile', label: 'Mobile', description: 'Vendor mobile phone number', isDefault: false },
  { key: 'fax', label: 'Fax', description: 'Vendor fax number', isDefault: false },
  { key: 'alternate_phone', label: 'Alternate Phone', description: 'Secondary phone number', isDefault: false },
  { key: 'email', label: 'Email', description: 'Vendor contact email', isDefault: true },
  { key: 'website', label: 'Website', description: 'Vendor website URL', isDefault: false },
  // { key: 'payment_method', label: 'Payment Method', description: 'Preferred payment method', isDefault: false },
  // { key: 'bank_account_info', label: 'Bank Account Info', description: 'Bank account details for payments', isDefault: false },
  { key: 'currency_preference', label: 'Currency Preference', description: 'Preferred currency for transactions', isDefault: false },
  { key: 'status', label: 'Status', description: 'Vendor lifecycle status', isDefault: false },
  // { key: 'tax_classification', label: 'Tax Classification', description: 'Tax classification category', isDefault: false },
  // { key: 'eligibility_1099', label: '1099 Eligibility', description: 'Whether vendor is eligible for 1099 reporting', isDefault: false },
];

// ===== Bill Creation Fields =====

export interface BillFieldOption {
  key: string;
  label: string;
  isDefault: boolean;
  alwaysOn?: boolean;
}

export const BILL_CREATION_FIELDS: BillFieldOption[] = [
  { key: 'invoice_id', label: 'Invoice Number', isDefault: true, alwaysOn: true },
  { key: 'vendor_id', label: 'Vendor', isDefault: true, alwaysOn: true },
  { key: 'posting_amount', label: 'Total Amount', isDefault: true, alwaysOn: true },
  { key: 'tax_total', label: 'Tax', isDefault: true },
  { key: 'due_date', label: 'Due Date', isDefault: true },
  { key: 'payment_terms', label: 'Payment Terms', isDefault: true },
  { key: 'currency', label: 'Currency', isDefault: true },
  { key: 'line_items.description', label: 'Line Item Description', isDefault: true },
  { key: 'line_items.quantity', label: 'Line Item Quantity', isDefault: true },
  { key: 'line_items.unit_price', label: 'Line Item Unit Price', isDefault: true },
  { key: 'line_items.total', label: 'Line Item Amount', isDefault: true },
];

// ===== ERP Providers =====

export interface ErpProvider {
  key: string;
  label: string;
  available: boolean;
}

export const ERP_PROVIDERS: ErpProvider[] = [
  { key: 'quickbooks', label: 'QuickBooks Online', available: true },
  { key: 'xero', label: 'Xero', available: true },
  { key: 'ajera', label: 'Deltek Ajera', available: true },
  { key: 'sage', label: 'Sage', available: false },
  { key: 'netsuite', label: 'NetSuite', available: true },
];

export type ErpProviderKey = (typeof ERP_PROVIDERS)[number]['key'];

// ===== Mailbox Providers =====

export interface MailboxProvider {
  key: string;
  label: string;
  available: boolean;
}

export const MAILBOX_PROVIDERS: MailboxProvider[] = [
  { key: 'microsoft_graph', label: 'Microsoft 365 (Graph API)', available: true },
  { key: 'gmail', label: 'Gmail', available: false },
];

export type MailboxProviderKey = (typeof MAILBOX_PROVIDERS)[number]['key'];

export interface MailboxRules {
  from_senders: string[];
  attachments_only: boolean;
  subject_contains: string;
}

// ===== Onboarding Config (POST payload) =====

export interface OnboardingConfig {
  extraction_fields: {
    purchase_order: string[];
    receipt: string[];
    invoice: string[];
  };
  erp_integration?: {
    erp_type: 'quickbooks' | 'netsuite' | 'xero' | 'ajera';
    is_connected: boolean;
  };
  mailbox_integration?: {
    provider: 'microsoft_graph';
    folder: string;
    rules: MailboxRules;
  };
  vendor_fields: string[];
  bill_creation_fields: string[];
}

// ===== Wizard Steps =====

export const WIZARD_STEPS = [
  'welcome',
  'payment_method',
  'extraction_fields',
  'erp_integration',
  'mailbox_integration',
  'vendor_fields',
  'bill_creation_fields',
  'review',
] as const;

export type WizardStepId = (typeof WIZARD_STEPS)[number];

export interface WizardStepMeta {
  id: WizardStepId;
  title: string;
  description: string;
  skippable: boolean;
}

export const WIZARD_STEP_META: WizardStepMeta[] = [
  {
    id: 'welcome',
    title: 'Welcome',
    description: 'Get started with your AP automation setup',
    skippable: false,
  },
  {
    id: 'payment_method',
    title: 'Payment',
    description: 'Add a payment method to keep your subscription active',
    skippable: false,
  },
  {
    id: 'extraction_fields',
    title: 'Document Field Extraction',
    description: 'Choose which fields to extract from your documents',
    skippable: false,
  },
  {
    id: 'erp_integration',
    title: 'ERP Integration',
    description: 'Connect your accounting system',
    skippable: true,
  },
  {
    id: 'mailbox_integration',
    title: 'Mailbox Integration',
    description: 'Set up automatic document ingestion from email',
    skippable: true,
  },
  {
    id: 'vendor_fields',
    title: 'Vendor Fields',
    description: 'Configure which vendor information to track',
    skippable: false,
  },
  {
    id: 'bill_creation_fields',
    title: 'Bill Creation Fields',
    description: 'Select which fields appear when creating bills',
    skippable: false,
  },
  {
    id: 'review',
    title: 'Review & Finish',
    description: 'Review your configuration and complete setup',
    skippable: false,
  },
];
